// Fetch the public key from S3 and import it
async function fetchPublicKey(pem) {
  const b64 = pem
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s+/g, '')
  const binaryDer = Uint8Array.from(atob(b64), c => c.charCodeAt(0))

  return await crypto.subtle.importKey(
    'spki',
    binaryDer.buffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256'
    },
    false,
    ['encrypt']
  )
}

export async function encryptWithPublicKey(plainText, pem) {
  const publicKey = await fetchPublicKey(pem)
  const encodedText = new TextEncoder().encode(plainText)
  const encrypted = await window.crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP'
    },
    publicKey,
    encodedText
  )

  const hex = [...new Uint8Array(encrypted)]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')

  return `masked-${hex}-masked`
}

export function getShortId(str) {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i)
    hash = hash >>> 0
  }
  return hash.toString(36)
}

export function maskSensitiveData(policy, input) {
  const { detectionRegexes = [], maskingRegexes = [], redactionStrings = [] } = policy || {}

  let result = input

  detectionRegexes.forEach((detectionPattern, i) => {
    const detectionRegex = new RegExp(detectionPattern, 'g')
    const maskingPattern = maskingRegexes[i]
    const redaction = redactionStrings[i] || '*'

    result = result?.replace(detectionRegex, (match) => {
      const maskingRegex = new RegExp(maskingPattern, 'g')
      return match?.replace(maskingRegex, redaction)
    })
  })

  return result
}

export async function maskSensitiveDataWithEncryption(botPCIPolicy, input, pem) {
  if(Object.keys(botPCIPolicy).length === 0) {
    return input
  }
  let result = input
  const matches = new Map()

  for (let i = 0; i < botPCIPolicy.detectionRegexes.length; i++) {
    const detectRegex = new RegExp(botPCIPolicy.detectionRegexes[i], 'g')
    const maskRegex = new RegExp(botPCIPolicy.maskingRegexes[i], 'g')
    const redactionChar = botPCIPolicy.redactionStrings[i] || '*'

    let match
    while ((match = detectRegex.exec(input)) !== null) {
      const originalValue = match[0]

      // Avoid encrypting the same value twice
      if (!matches.has(originalValue)) {
        // Apply masking
        const masked = originalValue.replace(maskRegex, redactionChar)

        // Encrypt original value (not masked)
        const encrypted = await encryptWithPublicKey(originalValue, pem)
        const wrappedEncrypted = `text-${encrypted}-text`

        matches.set(originalValue, { masked, wrappedEncrypted })
      }
    }
  }

  // Replace in result string
  for (const [originalValue, { wrappedEncrypted }] of matches) {
    const safeRegex = new RegExp(escapeRegex(originalValue), 'g')
    result = result?.replace(safeRegex, wrappedEncrypted)
  }

  return result
}

// Escape special characters for RegExp usage
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}


